import { useState, useCallback } from 'react';
import { usePlatformAdminAuthStore } from '../../stores/platform-admin-auth.store';
import {
  batchOperationsService,
  BatchOperationResult,
  BatchPasswordResetDto,
  BatchUpdateOrganizationsDto,
  BatchDeleteUsersDto,
  BatchUpdateUsersDto,
  BatchStatistics,
} from '../../services/platform-admin/batch-operations.service';

export function useBatchOperations() {
  const token = usePlatformAdminAuthStore((state) => state.token);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getStatistics = useCallback(async (): Promise<BatchStatistics | null> => {
    if (!token) {
      setError('Not authenticated');
      return null;
    }

    setIsLoading(true);
    setError(null);

    try {
      const stats = await batchOperationsService.getStatistics(token);
      return stats;
    } catch (err: any) {
      setError(err.message || 'Failed to fetch statistics');
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  const batchPasswordReset = useCallback(
    async (data: BatchPasswordResetDto): Promise<BatchOperationResult | null> => {
      if (!token) {
        setError('Not authenticated');
        return null;
      }

      setIsLoading(true);
      setError(null);

      try {
        const result = await batchOperationsService.batchPasswordReset(token, data);
        return result;
      } catch (err: any) {
        setError(err.message || 'Failed to reset passwords');
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [token]
  );

  const batchUpdateOrganizations = useCallback(
    async (data: BatchUpdateOrganizationsDto): Promise<BatchOperationResult | null> => {
      if (!token) {
        setError('Not authenticated');
        return null;
      }

      setIsLoading(true);
      setError(null);

      try {
        const result = await batchOperationsService.batchUpdateOrganizations(token, data);
        return result;
      } catch (err: any) {
        setError(err.message || 'Failed to update organizations');
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [token]
  );

  const batchDeleteUsers = useCallback(
    async (data: BatchDeleteUsersDto): Promise<BatchOperationResult | null> => {
      if (!token) {
        setError('Not authenticated');
        return null;
      }

      setIsLoading(true);
      setError(null);

      try {
        const result = await batchOperationsService.batchDeleteUsers(token, data);
        return result;
      } catch (err: any) {
        setError(err.message || 'Failed to delete users');
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [token]
  );

  const batchUpdateUsers = useCallback(
    async (data: BatchUpdateUsersDto): Promise<BatchOperationResult | null> => {
      if (!token) {
        setError('Not authenticated');
        return null;
      }

      setIsLoading(true);
      setError(null);

      try {
        const result = await batchOperationsService.batchUpdateUsers(token, data);
        return result;
      } catch (err: any) {
        setError(err.message || 'Failed to update users');
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [token]
  );

  return {
    isLoading,
    error,
    getStatistics,
    batchPasswordReset,
    batchUpdateOrganizations,
    batchDeleteUsers,
    batchUpdateUsers,
  };
}
